import React, { useMemo } from 'react';
import {
    Box,
    Container,
    SpaceBetween,
    FormField,
    Input,
    Textarea,
    Button,
    Badge,
    ColumnLayout,
    Select,
} from '@amzn/awsui-components-console';
import { FeatureType, FEATURE_TYPE_CONFIG } from '../FeatureType';
import { isValidUrl } from 'src/utils/StringUtils';

// Number of past months to show in the release date picker (covers ~1 year of history)
export const MONTHS_IN_PAST = 12;
// Number of future months to allow for scheduling upcoming releases
const MONTHS_IN_FUTURE = 2;

interface FeatureFormData {
    title: string;
    description: string;
    type: FeatureType;
    isEditing: boolean;
}

interface FeatureFormProps {
    releaseYear: string;
    releaseMonth: string;
    highlights: string;
    learnMoreLink: string;
    features: FeatureFormData[];
    onReleaseYearChange: (value: string) => void;
    onReleaseMonthChange: (value: string) => void;
    onHighlightsChange: (value: string) => void;
    onLearnMoreLinkChange: (value: string) => void;
    onUpdateFeature: (index: number, field: keyof FeatureFormData, value: string | FeatureType | boolean) => void;
    onAddFeature: () => void;
    onRemoveFeature: (index: number) => void;
    onConfirmEdit: (index: number) => void;
    isEditMode?: boolean;
}

export const FeatureForm: React.FC<FeatureFormProps> = ({
	releaseYear,
	releaseMonth,
	highlights,
	learnMoreLink,
	features,
	onReleaseYearChange,
	onReleaseMonthChange,
	onHighlightsChange,
	onLearnMoreLinkChange,
	onUpdateFeature,
	onAddFeature,
	onRemoveFeature,
	onConfirmEdit,
	isEditMode,
}) => {
	const availableMonths = useMemo((): Array<{
		value: string;
		year: string;
		label: string;
	}> => {
		const months: Array<{ value: string; year: string; label: string }> = [];
		const now = new Date();
		for (let i = -MONTHS_IN_PAST; i <= MONTHS_IN_FUTURE; i++) {
			const date = new Date(now.getFullYear(), now.getMonth() + i);
			months.push({
				value: String(date.getMonth() + 1).padStart(2, '0'),
				year: String(date.getFullYear()),
				label: date.toLocaleDateString('en-US', { month: 'long' }),
			});
		}
		return months;
	}, []);

	const currentYearOptions = useMemo(
		() => [...new Set(availableMonths.map((m) => m.year))],
		[availableMonths]
	);

	return (
		<SpaceBetween size="l">
			<FormField label="Release Month & Year">
				<ColumnLayout columns={2}>
					<Select
						disabled={isEditMode}
						selectedOption={{
							label:
								availableMonths.find(
									(m) => m.year === releaseYear && m.value === releaseMonth
								)?.label || '',
							value: releaseMonth,
						}}
						onChange={({ detail }) =>
							onReleaseMonthChange(detail.selectedOption.value!)
						}
						options={availableMonths
							.filter((m) => m.year === releaseYear)
							.map((m) => ({ label: m.label, value: m.value }))}
					/>
					<Select
						disabled={isEditMode}
						selectedOption={{ label: releaseYear, value: releaseYear }}
						onChange={({ detail }) => {
							onReleaseYearChange(detail.selectedOption.value!);
							const validMonth = availableMonths.find(
								(m) => m.year === detail.selectedOption.value
							);
							if (
								validMonth &&
								!availableMonths.some(
									(m) =>
										m.year === detail.selectedOption.value &&
										m.value === releaseMonth
								)
							) {
								onReleaseMonthChange(validMonth.value);
							}
						}}
						options={currentYearOptions.map((year) => ({
							label: year,
							value: year,
						}))}
					/>
				</ColumnLayout>
			</FormField>

			<Box>
				<Box variant="h3" padding={{ bottom: 'xs' }}>
					Highlights
					<Box variant="span" color="text-body-secondary" fontSize="body-s">
						{' '}
						(Enter one per line, numbered format)
					</Box>
				</Box>
				<Textarea
					value={highlights}
					onChange={({ detail }) => onHighlightsChange(detail.value)}
					rows={5}
					placeholder={
						'1. New dashboard with real-time analytics\n2. Performance improvements - 50% faster load times\n3. Enhanced search with filters'
					}
				/>
			</Box>

			<Box>
				<Box variant="h3" padding={{ bottom: 'xs' }}>
					Features
				</Box>
				<Container>
					<SpaceBetween size="s">
						{features.map((feature, index) => (
							<Container key={index}>
								<SpaceBetween size="xs">
									<ColumnLayout columns={2}>
										<SpaceBetween direction="horizontal" size="xs">
											<Badge color={FEATURE_TYPE_CONFIG[feature.type].color}>
												{FEATURE_TYPE_CONFIG[feature.type].label}
											</Badge>
											{feature.isEditing ? (
												<Input
													value={feature.title}
													placeholder="Feature title"
													onChange={({ detail }) =>
														onUpdateFeature(index, 'title', detail.value)
													}
												/>
											) : (
												<Box variant="h4">
													{feature.title || 'Feature title'}
												</Box>
											)}
										</SpaceBetween>
										<Box float="right">
											<SpaceBetween direction="horizontal" size="xs">
												<Button
													variant="inline-icon"
													iconName="edit"
													onClick={() =>
														onUpdateFeature(index, 'isEditing', true)
													}
													disabled={feature.isEditing}
												/>
												<Button
													variant="inline-icon"
													iconName="remove"
													onClick={() => onRemoveFeature(index)}
													disabled={features.length === 1}
												/>
											</SpaceBetween>
										</Box>
									</ColumnLayout>

									{feature.isEditing ? (
										<Textarea
											value={feature.description}
											rows={2}
											placeholder="Feature description"
											onChange={({ detail }) =>
												onUpdateFeature(index, 'description', detail.value)
											}
										/>
									) : (
										<Box color="text-body-secondary" fontSize="body-s">
											{feature.description || 'Feature description'}
										</Box>
									)}

									{feature.isEditing && (
										<ColumnLayout columns={2}>
											<Select
												selectedOption={{
													label: FEATURE_TYPE_CONFIG[feature.type].label,
													value: feature.type,
												}}
												onChange={({ detail }) =>
													onUpdateFeature(
														index,
														'type',
														detail.selectedOption.value as FeatureType
													)
												}
												options={[
													{ label: 'NEW', value: FeatureType.NEW },
													{
														label: 'IMPROVEMENT',
														value: FeatureType.IMPROVEMENT,
													},
													{
														label: 'COMING SOON',
														value: FeatureType.COMING_SOON,
													},
													{ label: 'FIX', value: FeatureType.FIX },
												]}
											/>
											<Box float="right">
												<Button
													variant="inline-icon"
													iconName="status-positive"
													onClick={() => onConfirmEdit(index)}
												/>
											</Box>
										</ColumnLayout>
									)}
								</SpaceBetween>
							</Container>
						))}
						<Button iconName="add-plus" onClick={onAddFeature}>
							Add Feature
						</Button>
					</SpaceBetween>
				</Container>
			</Box>

			<FormField label="Learn More" description="(Optional)">
				<ColumnLayout columns={2}>
					<Input
						value={learnMoreLink}
						type="url"
						placeholder="https://w.amazon.com/bin/view/SNOW/ReleaseNotes"
						onChange={({ detail }) => onLearnMoreLinkChange(detail.value)}
					/>
					<Button
						variant="inline-icon"
						iconName="external"
						onClick={() => window.open(learnMoreLink, '_blank')}
						disabled={!learnMoreLink || !isValidUrl(learnMoreLink)}
					/>
				</ColumnLayout>
			</FormField>
		</SpaceBetween>
	);
};
